import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";

/**
 * Only `en` is bundled. The other five locales are ~413 kB of raw JSON that every
 * visitor used to download and parse regardless of the language they read: the six
 * files were 526 kB of the 988 kB entry chunk (measured 2026-07-31). They now load
 * on demand.
 *
 * `en` stays static on purpose. It is the `fallbackLng`, so it must be present
 * synchronously, and keeping it bundled means the common case costs zero extra
 * requests.
 */
const LOCALE_LOADERS: Record<string, () => Promise<{ default: unknown }>> = {
  fr: () => import("./locales/fr.json"),
  es: () => import("./locales/es.json"),
  pa: () => import("./locales/pa.json"),
  hi: () => import("./locales/hi.json"),
  ur: () => import("./locales/ur.json"),
};

const loadedLanguages = new Set<string>(["en"]);
const inFlight = new Map<string, Promise<void>>();

/**
 * Load a locale bundle into i18next if it isn't already there. Idempotent, and
 * deliberately NON-FATAL: a failed chunk fetch (stale deploy, offline blip) leaves
 * the caller on the `en` fallback rather than throwing. Swallowing the rejection
 * here also keeps it away from the `unhandledrejection` stale-chunk handler in
 * main.tsx, which would otherwise try to reload the page over a missing locale.
 */
export async function ensureLanguageLoaded(language: string | undefined): Promise<void> {
  const code = (language ?? "en").split("-")[0].toLowerCase();
  if (loadedLanguages.has(code)) return;
  const existing = inFlight.get(code);
  if (existing) return existing;
  const loader = LOCALE_LOADERS[code];
  if (!loader) return;

  const task = loader()
    .then((mod) => {
      i18n.addResourceBundle(code, "translation", mod.default, true, true);
      loadedLanguages.add(code);
    })
    .catch(() => {
      // Stay on the en fallback. Deliberately silent: a missing translation file
      // must never blank the app or trigger a reload loop.
    })
    .finally(() => {
      inFlight.delete(code);
    });

  inFlight.set(code, task);
  return task;
}

/**
 * Supported language list. Flags map to the primary market for the language
 * (US English, Canadian-French, LatAm-Spanish-leaning, Indian-Punjabi, Indian
 * Hindi, Pakistani Urdu). The `dir` field defaults to "ltr" — Urdu sets it to
 * "rtl" and the runtime hook below flips <html dir> to match.
 */
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", flag: "🇺🇸", dir: "ltr" as const },
  { code: "fr", label: "Français", flag: "🇨🇦", dir: "ltr" as const },
  { code: "es", label: "Español", flag: "🇪🇸", dir: "ltr" as const },
  { code: "pa", label: "ਪੰਜਾਬੀ", flag: "🇮🇳", dir: "ltr" as const },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳", dir: "ltr" as const },
  { code: "ur", label: "اردو", flag: "🇵🇰", dir: "rtl" as const },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

/**
 * Map ISO 3166-1 alpha-2 country codes to a default UI language. Used by the
 * location-detection layer (Cloudflare CF-IPCountry + browser navigator) to
 * pick a sensible default the first time a user lands.
 *
 * Order of precedence for first-paint language:
 *   1. localStorage `cr-language` (user's explicit pick — sticky)
 *   2. URL `?lng=xx` (deep-link override)
 *   3. <meta name="cf-ipcountry" content="XX"> injected by the edge worker
 *      (server-side IP geo, ~50ms vs. ipapi roundtrip)
 *   4. navigator.language (e.g. "hi-IN", "ur-PK", "fr-CA")
 *   5. "en" fallback
 *
 * Detection #3 is wired by the LanguageDetector custom detector below.
 */
export const COUNTRY_TO_LANGUAGE: Record<string, SupportedLanguageCode> = {
  IN: "hi",     // India → Hindi
  PK: "ur",     // Pakistan → Urdu
  BD: "hi",     // Bangladesh → closest match (Hindi reads cross-script)
  NP: "hi",     // Nepal → Hindi
  // North-American Punjabi diaspora — keep en default unless user picks pa
  MX: "es",     // Mexico → Spanish
  ES: "es",     // Spain → Spanish
  AR: "es", CO: "es", PE: "es", VE: "es", CL: "es",
  FR: "fr",     // France → French
  CA: "en",     // Canada → English (Quebec users with fr-CA navigator will
                // be caught by the navigator detector and routed to fr)
};

// ── Custom detector: read CF-IPCountry from a meta tag if the edge worker
// injected one. Non-fatal — falls through to other detectors if absent. ──

const cfMetaCountryDetector = {
  name: "cfMetaCountry",
  lookup() {
    if (typeof document === "undefined") return undefined;
    const meta = document.querySelector(
      'meta[name="cf-ipcountry"]',
    ) as HTMLMetaElement | null;
    const country = meta?.content?.trim().toUpperCase();
    if (!country) return undefined;
    return COUNTRY_TO_LANGUAGE[country];
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    // `en` is bundled while the other locales arrive via ensureLanguageLoaded(), so
    // i18next must not assume a language is missing just because it wasn't in
    // `resources` at init.
    partialBundledLanguages: true,
    fallbackLng: "en",
    supportedLngs: ["en", "fr", "es", "pa", "hi", "ur"],
    // Map fr-CA / fr-FR / fr-anywhere to our generic `fr` bundle, hi-IN -> hi,
    // ur-PK -> ur, pa-IN -> pa, etc.
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      // Order of detectors: explicit user pick > deep-link > server-side IP geo
      // > browser language > html-lang fallback. localStorage is cached so
      // returning users always get their last pick.
      order: [
        "localStorage",
        "querystring",
        "cfMetaCountry",
        "navigator",
        "htmlTag",
      ],
      caches: ["localStorage"],
      lookupLocalStorage: "cr-language",
      lookupQuerystring: "lng",
    },
  });

// Wire the custom CF-IPCountry detector after init so it's available in the
// detector list above.
// deno-lint-ignore no-explicit-any
(i18n.services.languageDetector as any)?.addDetector?.(cfMetaCountryDetector);

// ── RTL handling ────────────────────────────────────────────────────────────
//
// When the active language is Urdu (or any future RTL locale we add), flip
// the <html dir> attribute so all CSS logical properties + Tailwind rtl:*
// variants apply. Also set <html lang> so screen readers announce the right
// language. Both attributes are read by the OS speech engine, so this matters
// for accessibility, not just visual layout.

function applyDirAndLang(language: string) {
  if (typeof document === "undefined") return;
  const code = language.split("-")[0].toLowerCase();
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  const dir = lang?.dir ?? "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", code);
}

// Apply on initial load (after i18n init resolves the active language)…
applyDirAndLang(i18n.language || "en");

// …and on every subsequent change (language switcher, deep link, etc).
i18n.on("languageChanged", applyDirAndLang);

// A later switch (LanguageSwitcher, QuebecLanguageBanner, useLocationLanguage) fires
// languageChanged before the bundle exists, so fetch it here too. i18next re-renders
// via react-i18next once addResourceBundle lands, so the switch briefly shows the en
// fallback instead of blanking the route.
i18n.on("languageChanged", (lng) => {
  void ensureLanguageLoaded(lng);
});

/**
 * Resolves once the language detected at boot is actually available.
 *
 * main.tsx awaits this before the first render. Without it, a visitor whose detected
 * language is fr/es/pa/hi/ur would see a flash of English on FIRST PAINT — a
 * regression the old all-locales-bundled setup did not have. English visitors resolve
 * immediately (no loader entry, no request), so the common path is unchanged.
 *
 * Never rejects: ensureLanguageLoaded swallows load failures and leaves the en
 * fallback in place, so a bad chunk cannot stop the app from mounting.
 */
export const i18nReady: Promise<void> = ensureLanguageLoaded(i18n.language);

export default i18n;
